import { Box, Button, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { format, isValid, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ListPageLayout from '../../../components/ListPageLayout';
import { LoadingContent } from '../../../components/Loading';
import { EmptyContentComponent } from '../../../components/Content';
import SelectClub from '../../../components/SelectClub';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';
import {
    SUBSCRIPTION_STATUS_OPTIONS,
    getSubscriptionStatusInfo,
} from '../../Club/utils/subscriptionStatus';

const SORT_OPTIONS = [
    { value: 'recent', label: 'Criação (mais recentes)' },
    { value: 'oldest', label: 'Criação (mais antigos)' },
    { value: 'name_asc', label: 'Nome (A–Z)' },
    { value: 'name_desc', label: 'Nome (Z–A)' },
    { value: 'id_desc', label: 'ID (maior)' },
    { value: 'id_asc', label: 'ID (menor)' },
];

function formatCreatedAt(value) {
    if (!value) return '—';
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        if (!isValid(d)) return '—';
        return format(d, 'dd/MM/yyyy', { locale: ptBR });
    } catch {
        return '—';
    }
}

const BENEFIT_TYPES = [
    { value: 'telemedicina', label: 'Telemedicina' },
    { value: 'telepsicologia', label: 'Telepsicologia' },
    { value: 'seguro', label: 'Seguro' },
    { value: 'telepet', label: 'Telepet' },
];

function subscriptionChipColor(info) {
    if (info.color === 'success') return 'success';
    if (info.color === 'warning') return 'warning';
    if (info.color === 'error') return 'error';
    return 'default';
}

function SubscriberRow({ item, viewMode }) {
    const statusInfo = getSubscriptionStatusInfo(item.subscription);
    const clubName = item.Club?.name || '—';
    const clubHref = item.clubId || item.Club?.id ? `/dashboard/clubes/${item.clubId || item.Club.id}` : null;

    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                    <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                            #{item.id}
                        </Typography>
                        <Typography fontWeight={700} noWrap>
                            {item.name || '—'}
                        </Typography>
                        {clubHref ? (
                            <Typography variant="body2" component={Link} to={clubHref} sx={{ color: 'primary.main' }}>
                                {clubName}
                            </Typography>
                        ) : (
                            <Typography variant="body2">{clubName}</Typography>
                        )}
                        <Chip size="small" label={statusInfo.label} color={subscriptionChipColor(statusInfo)} variant="outlined" />
                        <Typography variant="caption" color="text.secondary">
                            Criado {formatCreatedAt(item.createdAt)}
                        </Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="body2" sx={{ flex: 0.4, minWidth: 0 }} noWrap>
                        {item.id}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1.4, minWidth: 0 }} noWrap>
                        {item.name || '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1.2, minWidth: 0 }} noWrap>
                        {clubHref ? (
                            <Box component={Link} to={clubHref} sx={{ color: 'primary.main', textDecoration: 'none' }}>
                                {clubName}
                            </Box>
                        ) : (
                            clubName
                        )}
                    </Typography>
                    <Box sx={{ flex: 0.9, minWidth: 0 }}>
                        <Chip size="small" label={statusInfo.label} color={subscriptionChipColor(statusInfo)} variant="outlined" />
                    </Box>
                    <Typography variant="body2" sx={{ flex: 0.8, minWidth: 0 }} noWrap>
                        {formatCreatedAt(item.createdAt)}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function AdminSubscribers() {
    const [searchParams, setParams] = useSearchParams();
    const [download, setDownload] = useState(false);
    const { enqueueSnackbar, setErrors } = useErrors();
    const { search, input, handleChange, handleReset } = useSearchTextInput(searchParams.get('search') || '');

    const params = useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

    useEffect(() => {
        const current = searchParams.get('search') || '';
        if ((search || '') === current) return;
        const next = new URLSearchParams(searchParams);
        next.delete('page');
        if (search) next.set('search', search);
        else next.delete('search');
        setParams(next);
    }, [search]);
    const { data, isFetching } = useFetch('subscriber', { params });

    const handleClearFilters = () => {
        handleReset();
        setParams({});
    };

    const handlePage = (_, page) => {
        const next = new URLSearchParams(searchParams);
        next.set('page', page);
        setParams(next);
    };

    const handlePageSizeChange = nextSize => {
        const next = new URLSearchParams(searchParams);
        next.set('perPage', String(nextSize));
        next.set('page', '1');
        setParams(next);
    };

    const setFilter = (key, value) => {
        const next = new URLSearchParams(searchParams);
        next.delete('page');
        if (value === '' || value == null) next.delete(key);
        else next.set(key, String(value));
        setParams(next);
    };

    const pageSize = Number(searchParams.get('perPage') || searchParams.get('per_page')) || 10;
    const hasFilters = Array.from(searchParams.entries()).some(([key, value]) => {
        if (value === '' || value == null) return false;
        if (['page', 'perPage', 'per_page'].includes(key)) return false;
        if (key === 'sort' && (value === 'recent' || value === 'createdAt_desc')) return false;
        return true;
    });

    const handleExport = async () => {
        try {
            setDownload(true);
            const exportParams = { ...params };
            delete exportParams.page;
            delete exportParams.perPage;
            delete exportParams.per_page;
            const { data: blob } = await server.get('subscriber/export', {
                params: { ...exportParams, format: 'xlsx' },
                responseType: 'blob',
                timeout: 120000,
            });
            const url = window.URL.createObjectURL(new Blob([blob], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `assinantes-${Date.now()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            setErrors(error);
            enqueueSnackbar('Erro ao exportar assinantes.', { variant: 'error' });
        } finally {
            setDownload(false);
        }
    };

    const rows = data?.rows ?? [];
    const totalCount = data?.numRows ?? 0;

    return (
        <ListPageLayout
            title="Assinantes"
            subtitle="Todos os assinantes da rede, com filtro por clube, status da assinatura e benefício."
            counts={[{ label: 'assinantes', value: totalCount, color: 'primary' }]}
            loading={isFetching && !data}
            filterActive={hasFilters}
            onClearFilters={hasFilters ? handleClearFilters : null}
            viewStorageKey="admin-subscribers-view"
            actions={
                <Button size="small" variant="outlined" onClick={handleExport} disabled={download}>
                    {download ? 'Exportando…' : 'Exportar Excel'}
                </Button>
            }
            isEmpty={!isFetching && rows.length === 0}
            empty={
                <Stack spacing={2} alignItems="center">
                    <EmptyContentComponent />
                    <Typography color="text.secondary">
                        {hasFilters ? 'Nenhum assinante com esses filtros.' : 'Nenhum assinante cadastrado.'}
                    </Typography>
                    {hasFilters ? (
                        <Button variant="outlined" onClick={handleClearFilters}>
                            Limpar filtros
                        </Button>
                    ) : null}
                </Stack>
            }
            pagination={{
                page: data?.page || 1,
                pages: data?.pages || 1,
                total: totalCount,
                pageSize,
                onChange: handlePage,
                onPageSizeChange: handlePageSizeChange,
                itemLabel: totalCount === 1 ? 'assinante' : 'assinantes',
            }}
            filterPanel={({ close }) => (
                <Stack spacing={2}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Buscar"
                        placeholder="ID, nome, documento ou e-mail"
                        value={input}
                        onChange={e => handleChange(e.target.value)}
                    />
                    <SelectClub
                        size="small"
                        fullWidth
                        name="clubId"
                        value={searchParams.get('clubId') || ''}
                        onChange={e => setFilter('clubId', e.target.value)}
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel id="subscription-status-label">Status da assinatura</InputLabel>
                        <Select
                            labelId="subscription-status-label"
                            label="Status da assinatura"
                            value={searchParams.get('subscriptionStatus') || ''}
                            onChange={e => setFilter('subscriptionStatus', e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {SUBSCRIPTION_STATUS_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel id="benefit-type-label">Benefício</InputLabel>
                        <Select
                            labelId="benefit-type-label"
                            label="Benefício"
                            value={searchParams.get('benefitType') || ''}
                            onChange={e => setFilter('benefitType', e.target.value)}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {BENEFIT_TYPES.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                        <InputLabel id="sort-label">Ordenar por</InputLabel>
                        <Select
                            labelId="sort-label"
                            label="Ordenar por"
                            value={searchParams.get('sort') || 'recent'}
                            onChange={e => setFilter('sort', e.target.value === 'recent' ? '' : e.target.value)}
                        >
                            {SORT_OPTIONS.map(option => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        label="Criado de"
                        value={searchParams.get('createdFrom') || ''}
                        onChange={e => setFilter('createdFrom', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        fullWidth
                        type="date"
                        label="Criado até"
                        value={searchParams.get('createdTo') || ''}
                        onChange={e => setFilter('createdTo', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" fullWidth onClick={() => close?.()}>
                        Fechar filtros
                    </Button>
                </Stack>
            )}
        >
            {({ viewMode }) =>
                isFetching && !data ? (
                    <LoadingContent />
                ) : (
                    <Box sx={{ p: 2 }}>
                        {viewMode === 'list' && (
                            <Card variant="outlined" sx={{ mb: 1.5, display: { xs: 'none', md: 'block' } }}>
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 0.4 }}>
                                            ID
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1.4 }}>
                                            Nome
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 1.2 }}>
                                            Clube
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 0.9 }}>
                                            Status assinatura
                                        </Typography>
                                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ flex: 0.8 }}>
                                            Criado
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                        <Box
                            sx={{
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {rows.map(item => (
                                <SubscriberRow key={item.id} item={item} viewMode={viewMode} />
                            ))}
                        </Box>
                    </Box>
                )
            }
        </ListPageLayout>
    );
}
